import {
  Box,
  Button,
  ButtonGroup,
  FormControl,
  FormLabel,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Select,
  useBoolean,
  VStack,
} from "@chakra-ui/react";
import { useCallback, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { HiOutlineFilter } from "react-icons/hi";
import { useLocation } from "react-router-dom";

import {
  useGetAllDealerOrganizationsQuery,
  useGetAllIntercomModelsQuery,
  useGetAllPropertyOrganizationsQuery,
} from "src/routes/ProductUnits/Intercoms/queries";

type FilterFormValues = {
  intercomModel: string;
  dealerOrganization: string;
  propertyOrganization: string;
};

interface Props {
  onSubmit(searchParams: URLSearchParams): void;
}

export const IntercomsFilterPopover = ({ onSubmit }: Props) => {
  const [isEditing, setIsEditing] = useBoolean();
  const location = useLocation();
  const sp = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const { control, handleSubmit } = useForm<FilterFormValues>({
    defaultValues: {
      intercomModel: sp.get("intercomModel") || "",
      dealerOrganization: sp.get("dealerOrganization") || "",
      propertyOrganization: sp.get("propertyOrganization") || "",
    },
  });

  const parseParameters = useCallback(
    (data: FilterFormValues) => {
      const newSp = new URLSearchParams(location.search);

      for (const [key, value] of Object.entries(data)) {
        if (value) {
          newSp.set(key, value as string);
        } else {
          newSp.delete(key);
        }
      }

      onSubmit(newSp);
      setIsEditing.off();
    },
    [location.search]
  );

  const intercomModelsQuery = useGetAllIntercomModelsQuery();
  const dealerOrganizationsQuery = useGetAllDealerOrganizationsQuery();
  const propertyOrganizationsQuery = useGetAllPropertyOrganizationsQuery();

  return (
    <Popover
      isOpen={isEditing}
      onOpen={setIsEditing.on}
      onClose={setIsEditing.off}
      closeOnBlur={false}
      isLazy
      lazyBehavior="keepMounted"
    >
      <PopoverTrigger>
        <Button leftIcon={<HiOutlineFilter />}>Filters</Button>
      </PopoverTrigger>

      <PopoverContent display="flex">
        <PopoverHeader>Filters</PopoverHeader>
        <PopoverCloseButton />
        <PopoverBody>
          <form onSubmit={handleSubmit(parseParameters)}>
            <VStack align="start" gap="6">
              <Box width="100%">
                <Controller
                  name="intercomModel"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="intercomModelId">
                        Intercom Model
                      </FormLabel>
                      <Select
                        {...field}
                        id="intercomModelId"
                        isDisabled={intercomModelsQuery.isLoading}
                      >
                        <option value=""></option>
                        {intercomModelsQuery.data?.map((model) => (
                          <option
                            value={model.intercomModelId}
                            key={model.intercomModelId}
                          >
                            {model.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <Controller
                  name="dealerOrganization"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="dealerOrganizationId">
                        Dealer Organization
                      </FormLabel>
                      <Select
                        {...field}
                        id="dealerOrganizationId"
                        isDisabled={dealerOrganizationsQuery.isLoading}
                      >
                        <option value=""></option>
                        {dealerOrganizationsQuery.data?.map(
                          (dealerOrganization) => (
                            <option
                              value={dealerOrganization.dealerOrganizationId}
                              key={dealerOrganization.dealerOrganizationId}
                            >
                              {dealerOrganization.name}
                            </option>
                          )
                        )}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <Controller
                  name="propertyOrganization"
                  control={control}
                  render={({ field }) => (
                    <FormControl>
                      <FormLabel htmlFor="propertyOrganizationId">
                        Property Organization
                      </FormLabel>
                      <Select
                        {...field}
                        id="propertyOrganizationId"
                        isDisabled={propertyOrganizationsQuery.isLoading}
                      >
                        <option value=""></option>
                        {propertyOrganizationsQuery.data?.map(
                          (propertyOrganization) => (
                            <option
                              value={
                                propertyOrganization.propertyOrganizationId
                              }
                              key={propertyOrganization.propertyOrganizationId}
                            >
                              {propertyOrganization.name}
                            </option>
                          )
                        )}
                      </Select>
                    </FormControl>
                  )}
                />
              </Box>

              <Box width="100%">
                <ButtonGroup display="flex" justifyContent="flex-end">
                  <Button type="button" onClick={setIsEditing.off}>
                    Cancel
                  </Button>
                  <Button type="submit" colorScheme="brand.blue">
                    Apply Filters
                  </Button>
                </ButtonGroup>
              </Box>
            </VStack>
          </form>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
